import { useCallback } from 'react';
import { Factors, Passkeys } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { useConfirm } from '@beyond-js/ui/react';
import { useQuestions } from './questions';

/**
 * Asks the person to prove it is them again, in place, before a sensitive action is sent again (E8:
 * re-authentication for sensitive actions): a passkey in this browser when the account has one, else
 * the password, else an authenticator code, in the shared question over the page.
 *
 *     const reauthenticate = useReauthenticate();
 *     if (Refusal.is(error) && error.code === 'REAUTHENTICATION_REQUIRED' && (await reauthenticate())) return send();
 *
 * @returns Whether the person proved it, so the action may be sent again; `false` when they cancelled or
 * nothing in place is possible, and the screen then offers signing in again as before
 */
export function useReauthenticate(): () => Promise<boolean> {
	const { client, text } = usePresence();
	const { prompt } = useConfirm(useQuestions());
	return useCallback(async () => {
		const factors = new Factors(client);
		let methods: string[];
		try {
			methods = await factors.methods();
		} catch {
			return false;
		}
		if (methods.includes('passkey') && Passkeys.supported) {
			try {
				await factors.reauthenticate('passkey');
				return true;
			} catch {
				// Cancelled or not available here: another way follows
			}
		}
		const method = methods.includes('password') ? 'password' : methods.includes('totp') ? 'totp' : undefined;
		if (!method) return false;
		const value = await prompt({
			title: text('reauth.title'),
			message: text(`reauth.${method}`),
			label: text(`reauth.${method}.label`),
			type: method === 'password' ? 'password' : 'text',
			required: true,
			accept: text('reauth.accept'),
			cancel: text('action.cancel'),
			work: (given: unknown) => factors.reauthenticate(method, String(given)),
			explain: (error: unknown) => (Refusal.is(error) ? (error.code === 'FACTOR_INVALID' ? text(`reauth.${method}.wrong`) : text(error.key, error.values)) : text('error.unexpected'))
		});
		return value !== null;
	}, [client, prompt, text]);
}
